import React, { useEffect } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Button from "@material-ui/core/Button";

import moment from "moment";

import InspectionsLocal from "../inspections/inspectionslocal";
import SyncDialog from "../sync/SyncDialog";

import { Confirmation } from "../lib/components/confirmation";
import { genericApi, hasAnyStored } from "../lib/genericAPI";
import { inspectiontype } from "../lib/storage/inspection";
import { UserContext } from "../lib/context/UserContext";

const readLastSync = (): string | null => {
  const value = localStorage.getItem("lastSync");
  if (!value || value === "0") return null;
  return value;
};

const needsInitialSync = (): boolean => readLastSync() === null || !hasAnyStored("producers");

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing.unit,
    },
    banner: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      padding: theme.spacing.unit,
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing.unit,
      textAlign: "center",
      fontSize: "0.875rem",
    },
    actions: {
      marginTop: theme.spacing.unit * 2,
      paddingBottom: theme.spacing.unit * 8,
    },
    clearButton: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: theme.palette.error.dark,
      },
    },
  });

type HomeProps = {} & WithStyles<typeof styles>;

const HomeUnstyled: React.SFC<HomeProps> = (props) => {
  const { classes } = props;

  const { user } = React.useContext(UserContext);
  const isAuthenticated = Boolean(user && user.username);

  const [lastSync, setLastSync] = React.useState<string | null>(() => readLastSync());
  const [syncDialogOpen, setSyncDialogOpen] = React.useState<boolean>(false);
  const [hasAutoOpened, setHasAutoOpened] = React.useState<boolean>(false);

  const [pendingCount, setPendingCount] = React.useState(0);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  useEffect(() => {
    if (hasAutoOpened) return;
    if (!isAuthenticated) return;
    if (!needsInitialSync()) return;
    setSyncDialogOpen(true);
    setHasAutoOpened(true);
  }, [isAuthenticated, hasAutoOpened]);

  const refreshPendingCount = () => {
    const inspectionsGeneric = genericApi<inspectiontype>("inspections");
    inspectionsGeneric.all().then((data) => {
      setPendingCount(data.length);
    });
  };

  useEffect(() => {
    refreshPendingCount();
    const intervalId = setInterval(() => {
      refreshPendingCount();
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const handleSyncDialogClose = () => {
    setSyncDialogOpen(false);
    setLastSync(readLastSync());
  };

  const handleClearClick = () => {
    setConfirmOpen(true);
  };

  const handleCancelClear = () => {
    setConfirmOpen(false);
  };

  const handleConfirmClear = async () => {
    const inspectionsGeneric = genericApi<inspectiontype>("inspections");
    const data = await inspectionsGeneric.all();
    await Promise.all((data || []).map((item) => inspectionsGeneric.remove(item.id)));
    setConfirmOpen(false);
    refreshPendingCount();
  };

  return (
    <div className={classes.root}>
      {lastSync && <div className={classes.banner}>Last synced {moment(Number(lastSync)).fromNow()}</div>}

      <InspectionsLocal status={0} />
      <InspectionsLocal status={1} />

      {pendingCount > 0 && (
        <div className={classes.actions}>
          <Button variant="contained" onClick={handleClearClick} className={classes.clearButton}>
            clear inspections
          </Button>
        </div>
      )}

      <Confirmation
        isOpen={confirmOpen}
        handleClose={handleCancelClear}
        handleConfirm={handleConfirmClear}
        title="Clear Inspections"
        body={`This will permanently remove ${pendingCount} pending local inspection${pendingCount === 1 ? "" : "s"}. Are you sure?`}
      />

      <SyncDialog open={syncDialogOpen} onClose={handleSyncDialogClose} />
    </div>
  );
};

export default withStyles(styles)(HomeUnstyled);
